import { DiscloserMark } from '@/components/onboarding-v2/OnboardingFlowV2';

interface BrandLoaderProps {
  /** Mark size in px. Default 56 fits a typical between-pages full-bleed loader. */
  size?: number;
  /** Wrap the loader in a min-h-screen flex-center wrapper. Default true so
   *  callers can drop <BrandLoader /> as a top-level page replacement without
   *  re-implementing the page chrome. Set false to embed inline. */
  fullScreen?: boolean;
  /** Optional accessible label. Defaults to "Loading". */
  label?: string;
}

/**
 * Brand-aligned page-transition loader. Renders the DiscloserMark twice — a
 * faint gray "ghost" in the background, a brand-ink fill on top — and
 * animates the top layer's clip-path to fill from top to bottom (and drain
 * the same way) on a 1.8s loop. Reads as the disclosure "filling in",
 * matching the product metaphor.
 *
 * Reduced-motion users get a static filled mark — no animation.
 */
export function BrandLoader({ size = 64, fullScreen = true, label = 'Loading' }: BrandLoaderProps) {
  const inner = (
    <div
      role="status"
      aria-label={label}
      className="relative flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      {/* Ghost layer — faint outline so the user always sees the mark even
          while the fill is "drained". */}
      <span className="absolute inset-0 flex items-center justify-center" aria-hidden>
        <DiscloserMark size={size} fill="hsl(var(--brand-text-3) / 0.18)" />
      </span>

      {/* Fill layer — single quick fill on mount (700ms), then hold full and
          breathe gently. This intentionally does NOT loop the fill: route
          transitions usually resolve in 200-800ms, and a looping fill
          unmounted mid-cycle was reading as "stuck at 1/8 full." With
          forwards + a follow-on opacity breathe, the user sees the mark
          *always* land at full whenever the loader disappears. */}
      <span
        className="absolute inset-0 flex items-center justify-center motion-reduce:!animate-none"
        style={{
          animation:
            'brand-loader-fill 0.7s ease-out forwards, brand-loader-breathe 2.4s ease-in-out 0.7s infinite',
        }}
        aria-hidden
      >
        <DiscloserMark size={size} fill="hsl(var(--brand-ink))" />
      </span>

      <style>{`
        @keyframes brand-loader-fill {
          0%   { clip-path: inset(0 0 100% 0); }
          100% { clip-path: inset(0 0 0% 0); }
        }
        @keyframes brand-loader-breathe {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.78; }
        }
      `}</style>
    </div>
  );

  if (!fullScreen) return inner;

  return (
    <div className="min-h-screen bg-brand-paper-warm font-brand text-brand-ink flex items-center justify-center">
      {inner}
    </div>
  );
}
